import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Typography, Avatar, FormControl, InputLabel, Select, MenuItem, Button, Card, CardContent } from '@mui/material';
import { Users, CheckCircle, GraduationCap, ArrowRightLeft, UserMinus, UserPlus, LogIn, Activity, TrendingUp, Award, Calendar, BarChart3, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { Loader } from '@/components/feedback/Loader';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuth } from '@/hooks/useAuth';
import { useSchool } from '@/hooks/useSchool';
import { ROLES } from '@/constants/roles';
import useStudentStore from '@/store/studentStore';
import { academicSessionsApi } from '@/api/academicSessions.api';
import { useNavigate } from 'react-router-dom';
import { tokens } from '@/theme/tokens';

// Status styling & icons
const STATUS_CONFIG = {
  ACTIVE: { label: 'Active', icon: CheckCircle, color: '#16A34A' },
  INACTIVE: { label: 'Inactive', icon: AlertCircle, color: '#F59E0B' },
  TRANSFERRED: { label: 'Transferred', icon: ArrowRightLeft, color: '#2563EB' },
  PASSED_OUT: { label: 'Passed Out', icon: GraduationCap, color: '#8B5CF6' },
  DROPPED_OUT: { label: 'Dropped Out', icon: UserMinus, color: '#DC2626' },
};

const ALL_STATUS_KEYS = ['ACTIVE', 'INACTIVE', 'TRANSFERRED', 'PASSED_OUT', 'DROPPED_OUT'];
const GENDER_COLORS = { MALE: '#2563EB', FEMALE: '#EC4899', OTHER: '#F59E0B' };
const CHART_COLOR = '#3B82F6';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// Unified stat & status card with consistent sizing and typography
function DashboardStatCard({ title, value, subtext, subtextColor, icon: Icon, color = 'primary', onClick }) {
  const colorMap = {
    primary: tokens.colors.primary.main,
    success: tokens.colors.success.main,
    warning: tokens.colors.warning.main,
    error: tokens.colors.error.main,
    info: tokens.colors.info.main,
    secondary: '#EC4899',
    purple: '#8B5CF6',
  };
  const themeColor = colorMap[color] || (typeof color === 'string' && color.startsWith('#') ? color : tokens.colors.primary.main);

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        minHeight: 112,
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        border: `1px solid ${tokens.colors.border}`,
        boxShadow: tokens.shadows.card,
        '&:hover': {
          boxShadow: tokens.shadows.md,
          transform: onClick ? 'translateY(-2px)' : 'none',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: tokens.colors.text.muted,
                mb: 0.5,
                fontWeight: 500,
                fontSize: '0.8125rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: tokens.colors.text.primary,
                lineHeight: 1.2,
                fontSize: '1.45rem',
                mb: 0.5,
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: subtextColor || tokens.colors.text.muted,
                fontWeight: subtextColor ? 600 : 500,
                fontSize: '0.75rem',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.2,
              }}
            >
              {subtext || '\u00A0'}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: tokens.radius.md,
              backgroundColor: `${themeColor}14`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ml: 1.5,
            }}
          >
            <Icon size={22} color={themeColor} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// Insight row for Quick Insights widget
function InsightRow({ icon: Icon, title, value, color }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75,
        borderRadius: tokens.radius.md,
        backgroundColor: tokens.colors.background,
        border: `1px solid ${tokens.colors.border}`,
      }}
    >
      <Box
        sx={{
          width: 38, height: 38, borderRadius: tokens.radius.full,
          backgroundColor: `${color}14`, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <Icon size={19} color={color} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: tokens.colors.text.muted, fontWeight: 500, display: 'block', mb: 0.25 }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600, color: tokens.colors.text.primary,
            lineHeight: 1.35, wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// Custom tooltip displaying point title and payload values
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const firstData = payload[0]?.payload;
  const title = firstData?.fullDateLabel || (firstData?.year ? `${firstData.label} ${firstData.year}` : (firstData?.class_name || label));

  return (
    <Box
      sx={{
        backgroundColor: tokens.colors.card, p: 1.5,
        borderRadius: tokens.radius.sm, boxShadow: tokens.shadows.md,
        border: `1px solid ${tokens.colors.border}`,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: tokens.colors.text.primary }}>
        {title}
      </Typography>
      {payload.map((item, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block', color: item.color || tokens.colors.text.secondary }}>
          {item.name}: <strong style={{ color: tokens.colors.text.primary }}>{item.value}</strong>
        </Typography>
      ))}
    </Box>
  );
}

// Top label on bar chart columns
const BarLabel = ({ x, y, width, value }) => (
  <text x={x + width / 2} y={y - 6} fill={tokens.colors.text.secondary} textAnchor="middle" fontSize={11} fontWeight={600}>
    {value}
  </text>
);

// In-slice percentage label for donut chart
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);

  if (percent < 0.03) return null;
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

// Recent admissions table styles
const TABLE_SX = {
  width: '100%', borderCollapse: 'collapse',
  '& th': {
    py: 1.25, px: 1, textAlign: 'left', fontSize: '0.75rem', fontWeight: 600,
    color: tokens.colors.text.muted, borderBottom: `1px solid ${tokens.colors.border}`,
    whiteSpace: 'nowrap',
  },
  '& td': {
    py: 1.25, px: 1, fontSize: '0.8125rem', color: tokens.colors.text.primary,
    borderBottom: `1px solid ${tokens.colors.border}`, whiteSpace: 'nowrap',
  },
  '& tbody tr:hover': { backgroundColor: tokens.colors.grey[50], cursor: 'pointer' },
  '& tbody tr:last-child td': { borderBottom: 'none' },
};

export function StudentDashboardPage() {
  usePageMeta('Student Management', [
    { label: 'Students', path: '/students' },
    { label: 'Overview' },
  ]);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { schoolId } = useSchool();

  const dashboardStats = useStudentStore((s) => s.dashboardStats);
  const fetchDashboardStats = useStudentStore((s) => s.fetchDashboardStats);
  const loading = useStudentStore((s) => s.loading);
  const error = useStudentStore((s) => s.error);

  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');

  // Fetch academic sessions and initial dashboard data
  useEffect(() => {
    let alive = true;
    async function init() {
      if (!schoolId) return;
      try {
        useStudentStore.setState({ loading: true, error: null });
        const data = await academicSessionsApi.getAllBySchool(schoolId);
        const list = Array.isArray(data) ? data : data?.data || data?.items || [];
        if (!alive) return;
        setSessions(list);

        const current = list.find((s) => s.is_current) || list[0];
        const activeId = current?.id || current?.session_id;

        if (activeId) {
          setSelectedSessionId(activeId);
          await fetchDashboardStats(schoolId, activeId);
        } else {
          useStudentStore.setState({
            error: 'No active academic session found. Please create one first.',
            loading: false,
          });
        }
      } catch (err) {
        if (!alive) return;
        console.error('Failed to initialize student dashboard:', err);
        useStudentStore.setState({ error: err?.message || 'Failed to load academic sessions', loading: false });
      }
    }

    init();
    return () => { alive = false; };
  }, [schoolId, fetchDashboardStats]);

  const handleSessionChange = useCallback(
    (id) => {
      setSelectedSessionId(id);
      if (schoolId && id) fetchDashboardStats(schoolId, id);
    },
    [schoolId, fetchDashboardStats],
  );

  // Extracted dashboard data
  const summary = dashboardStats?.summary || {};
  const statusDistribution = dashboardStats?.status_distribution || [];
  const genderDistribution = dashboardStats?.gender_distribution || [];
  const classWiseStrength = dashboardStats?.class_wise_strength || [];
  const admissionTrendRaw = dashboardStats?.admission_trend || [];
  const recentAdmissions = dashboardStats?.recent_admissions || [];

  // Format admission trend with unique Month + Year labels directly from backend data
  const admissionTrend = useMemo(() => {
    return admissionTrendRaw.map((item) => {
      const yearLabel = item.year ? `${item.label} ${item.year}` : item.label;
      return {
        ...item,
        chartLabel: yearLabel,
        fullDateLabel: yearLabel,
      };
    });
  }, [admissionTrendRaw]);

  // Fill in any missing status keys to ensure 5 consistent cards
  const allStatuses = useMemo(() => {
    const map = Object.fromEntries(statusDistribution.map((s) => [s.status, s]));
    return ALL_STATUS_KEYS.map((key) => ({
      status: key,
      label: STATUS_CONFIG[key].label,
      count: map[key]?.count ?? (key === 'TRANSFERRED' ? (summary.transferred_students ?? 0) : 0),
      percentage: map[key]?.percentage ?? 0,
    }));
  }, [statusDistribution, summary]);

  // Gender donut chart data
  const genderChartData = useMemo(() => {
    const data = genderDistribution.map((g) => ({
      name: g.label,
      value: g.count,
      percentage: g.percentage,
      color: GENDER_COLORS[g.gender] || GENDER_COLORS.OTHER,
    }));
    if (summary.other_gender > 0) {
      data.push({
        name: 'Other',
        value: summary.other_gender,
        percentage: +((summary.other_gender / (summary.total_students || 1)) * 100).toFixed(2),
        color: GENDER_COLORS.OTHER,
      });
    }
    return data;
  }, [genderDistribution, summary]);

  // Quick insights highlights
  const insights = useMemo(() => {
    if (!classWiseStrength.length) return [];
    const sorted = [...classWiseStrength].sort((a, b) => b.total_students - a.total_students);
    const most = sorted[0];
    const least = sorted[sorted.length - 1];
    const latest = recentAdmissions[0];

    return [
      { icon: Award, title: 'Most Strength', value: `${most.class_name} – ${most.total_students} Students`, color: '#2563EB' },
      { icon: BarChart3, title: 'Least Strength', value: `${least.class_name} – ${least.total_students} Students`, color: '#F59E0B' },
      { icon: Calendar, title: 'Latest Admission', value: latest ? `${latest.student_name} (${formatDate(latest.admission_date)})` : 'None', color: '#16A34A' },
      { icon: TrendingUp, title: 'New Admissions', value: `${summary.new_admissions ?? 0} this session`, color: '#8B5CF6' },
    ];
  }, [classWiseStrength, recentAdmissions, summary]);

  const canManage =
    user?.role === ROLES.ORGANIZATION_ADMIN ||
    user?.role === ROLES.PRINCIPAL ||
    user?.role === ROLES.RECEPTION;

  if (loading) return <Loader message="Loading student dashboard..." />;

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader title="Student Dashboard" subtitle="Overview of students and academic information" />
        <WidgetContainer sx={{ mt: 3, p: 4 }}>
          <ErrorState
            title="Failed to Load Student Dashboard"
            message={error}
            onRetry={() => {
              if (schoolId && selectedSessionId) fetchDashboardStats(schoolId, selectedSessionId);
            }}
          />
        </WidgetContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {/* Page Header with Session Selector */}
      <PageHeader
        title="Student Dashboard"
        subtitle="Overview of students and academic information"
        actions={
          sessions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Academic Session</InputLabel>
              <Select
                value={selectedSessionId}
                label="Academic Session"
                onChange={(e) => handleSessionChange(e.target.value)}
              >
                {sessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.session_name || `${s.start_date} – ${s.end_date}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        }
      />

      {/* Row 1: Primary Stat Cards */}
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap={1.75} sx={{ mb: 0 }}>
        <DashboardStatCard
          title="Total Students"
          value={summary.total_students ?? 0}
          subtext="Total Enrolled"
          subtextColor={tokens.colors.primary.main}
          icon={Users}
          color="primary"
          onClick={() => navigate('/students/list')}
        />
        <DashboardStatCard
          title="Active Students"
          value={summary.active_students ?? 0}
          subtext={`${((summary.active_students / (summary.total_students || 1)) * 100).toFixed(1)}% of total`}
          subtextColor={tokens.colors.success.main}
          icon={CheckCircle}
          color="success"
          onClick={() => navigate('/students/list', { state: { status: 'active' } })}
        />
        <DashboardStatCard
          title="Boys"
          value={summary.boys ?? 0}
          subtext={`${((summary.boys / (summary.total_students || 1)) * 100).toFixed(1)}% of total`}
          subtextColor={tokens.colors.primary.main}
          icon={Users}
          color="info"
          onClick={() => navigate('/students/list', { state: { gender: 'Male' } })}
        />
        <DashboardStatCard
          title="Girls"
          value={summary.girls ?? 0}
          subtext={`${((summary.girls / (summary.total_students || 1)) * 100).toFixed(1)}% of total`}
          subtextColor="#EC4899"
          icon={Users}
          color="secondary"
          onClick={() => navigate('/students/list', { state: { gender: 'Female' } })}
        />
        <DashboardStatCard
          title="Other Gender"
          value={summary.other_gender ?? 0}
          subtext={`${((summary.other_gender / (summary.total_students || 1)) * 100).toFixed(1)}% of total`}
          subtextColor={tokens.colors.warning.main}
          icon={Users}
          color="warning"
        />
      </DashboardGrid>

      {/* Row 2: Status Distribution Cards */}
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap={1.75} sx={{ mb: 0 }}>
        {allStatuses.map((s) => (
          <DashboardStatCard
            key={s.status}
            title={s.label}
            value={s.count}
            subtext={`${s.percentage}% of total`}
            subtextColor={STATUS_CONFIG[s.status]?.color}
            icon={STATUS_CONFIG[s.status]?.icon || CheckCircle}
            color={STATUS_CONFIG[s.status]?.color}
            onClick={() => navigate('/students/list', { state: { status: s.status.toLowerCase() } })}
          />
        ))}
      </DashboardGrid>

      {/* Row 3: Class Wise Strength + Gender Donut */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 1.75 }}>
        <WidgetContainer
          title="Class Wise Strength"
          action={
            <Button size="small" variant="outlined" onClick={() => navigate('/students/list')}>
              View All
            </Button>
          }
        >
          <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={classWiseStrength} margin={{ top: 20, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.border} />
                <XAxis
                  dataKey="class_name" tick={{ fontSize: 11, fill: tokens.colors.text.muted }}
                  axisLine={false} tickLine={false} interval={0}
                  angle={-35} textAnchor="end" height={50}
                />
                <YAxis tick={{ fontSize: 11, fill: tokens.colors.text.muted }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar
                  dataKey="total_students" name="No. of Students"
                  fill={CHART_COLOR} radius={[4, 4, 0, 0]}
                  label={<BarLabel />} maxBarSize={40}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </WidgetContainer>

        <WidgetContainer
          title="Students by Gender"
          action={
            <Button size="small" sx={{ textTransform: 'none', color: tokens.colors.primary.main }}>
              View Details
            </Button>
          }
        >
          <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {genderChartData.length > 0 ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={genderChartData}
                    cx="50%" cy="45%"
                    innerRadius={55} outerRadius={95}
                    paddingAngle={3} dataKey="value"
                    label={PieLabel} labelLine={false}
                  >
                    {genderChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle" iconSize={10}
                    formatter={(val) => {
                      const item = genderChartData.find((g) => g.name === val);
                      return (
                        <span style={{ fontSize: 12, color: tokens.colors.text.secondary }}>
                          {val}&nbsp;&nbsp;{item?.value ?? 0} ({item?.percentage ?? 0}%)
                        </span>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No gender data available</Typography>
            )}
          </Box>
        </WidgetContainer>
      </Box>

      {/* Row 4: Recent Admissions + Admission Trend + Quick Insights */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.45fr 0.9fr 0.65fr' }, gap: 1.75 }}>
        <WidgetContainer
          title="Recent Admissions"
          noPadding
          action={
            <Button
              size="small"
              sx={{ textTransform: 'none', color: tokens.colors.primary.main }}
              onClick={() => navigate('/students/list')}
            >
              View All
            </Button>
          }
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={TABLE_SX}>
              <thead>
                <tr>
                  <th style={{ width: 28, textAlign: 'center' }}>#</th>
                  <th>Admission No.</th>
                  <th>Student Name</th>
                  <th>Admission Date</th>
                  <th>Class</th>
                </tr>
              </thead>
              <tbody>
                {recentAdmissions.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: tokens.colors.text.muted, py: 3 }}>
                      No recent admissions
                    </td>
                  </tr>
                ) : (
                  recentAdmissions.map((stu, idx) => (
                    <tr key={stu.student_id} onClick={() => navigate(`/students/${stu.student_id}`)}>
                      <td style={{ width: 28, textAlign: 'center', color: tokens.colors.text.muted, fontSize: '0.75rem' }}>
                        {idx + 1}
                      </td>
                      <td>
                        <Typography
                          variant="caption"
                          sx={{ color: tokens.colors.primary.main, fontWeight: 600, letterSpacing: '-0.01em' }}
                        >
                          {stu.admission_no}
                        </Typography>
                      </td>
                      <td>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              width: 22, height: 22, fontSize: '0.65rem',
                              bgcolor: tokens.colors.primary[100],
                              color: tokens.colors.primary.main,
                              flexShrink: 0,
                            }}
                          >
                            {stu.student_name?.[0]}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {stu.student_name}
                          </Typography>
                        </Box>
                      </td>
                      <td>
                        <Typography variant="caption" sx={{ color: tokens.colors.text.secondary, whiteSpace: 'nowrap' }}>
                          {formatDate(stu.admission_date)}
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.text.secondary, whiteSpace: 'nowrap' }}>
                          {stu.class?.name || '—'}
                        </Typography>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Box>
          </Box>
        </WidgetContainer>

        <WidgetContainer title="Admission Trend">
          <Box sx={{ width: '100%', height: 260 }}>
            {admissionTrend.length > 0 ? (
              <ResponsiveContainer>
                <AreaChart data={admissionTrend} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="admGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLOR} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLOR} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.border} />
                  <XAxis
                    dataKey="chartLabel"
                    tick={{ fontSize: 10, fill: tokens.colors.text.muted }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={45}
                  />
                  <YAxis tick={{ fontSize: 11, fill: tokens.colors.text.muted }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone" dataKey="admissions" name="Admissions"
                    stroke={CHART_COLOR} fill="url(#admGrad)" strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">No trend data available</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 2, borderTop: `1px solid ${tokens.colors.border}` }}>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted }}>This Session</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: tokens.colors.primary.main }}>
                {summary.new_admissions ?? 0}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted }}>Avg. Strength</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {summary.average_class_strength ?? 0}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted }}>RTE Students</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: tokens.colors.success.main }}>
                {summary.rte_students ?? 0}
              </Typography>
            </Box>
          </Box>
        </WidgetContainer>

        <WidgetContainer title="Quick Insights">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {insights.map((item, i) => (
              <InsightRow key={i} {...item} />
            ))}
          </Box>
        </WidgetContainer>
      </Box>

      {/* Quick Actions (Admin/Principal/Reception only) */}
      {canManage && (
        <WidgetContainer title="Quick Actions">
          <Box sx={{ p: 1 }}>
            <DashboardGrid columns={{ xs: 1, sm: 2, md: 4 }} gap={1}>
              <QuickActionCard title="View All Students" icon={Users} path="/students/list" color="primary" />
              <QuickActionCard title="Add New Student" icon={UserPlus} path="/students/add" color="success" />
              <QuickActionCard title="Import Students" icon={LogIn} color="secondary" />
              <QuickActionCard title="Student Reports" icon={Activity} color="warning" />
            </DashboardGrid>
          </Box>
        </WidgetContainer>
      )}
    </Box>
  );
}
